import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import { evmAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

export default function EVMDashboard() {
  const { data: evmData, isLoading } = useQuery({
    queryKey: ['evm-dashboard'],
    queryFn: () => evmAPI.getEVMDashboard(),
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const portfolio = evmData?.portfolio || {};
  const projects = evmData?.projects || [];

  const getHealthColor = (value, threshold = 0.95, critical = 0.85) => {
    if (value >= threshold) return 'text-green-600 bg-green-50';
    if (value >= critical) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusColor = (status) => {
    const colors = {
      on_track: 'bg-green-100 text-green-800',
      behind_schedule: 'bg-yellow-100 text-yellow-800',
      over_budget: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Earned Value Management Dashboard
        </h2>
        <p className="text-gray-600">
          Professional project performance measurement and forecasting
        </p>
      </div>

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Schedule Performance</h3>
            <ChartBarIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div className={`text-3xl font-bold ${getHealthColor(portfolio.average_spi || 0)}`}>
            {(portfolio.average_spi || 0).toFixed(2)}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            SPI {portfolio.average_spi >= 1 ? '(On Schedule)' : '(Behind)'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Cost Performance</h3>
            <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
          </div>
          <div className={`text-3xl font-bold ${getHealthColor(portfolio.average_cpi || 0)}`}>
            {(portfolio.average_cpi || 0).toFixed(2)}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            CPI {portfolio.average_cpi >= 1 ? '(Under Budget)' : '(Over Budget)'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Budget Variance</h3>
            {portfolio.total_variance >= 0 ? (
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
            ) : (
              <ArrowTrendingDownIcon className="h-5 w-5 text-red-600" />
            )}
          </div>
          <div className={`text-3xl font-bold ${portfolio.total_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ${Math.abs(portfolio.total_variance || 0).toLocaleString()}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {portfolio.total_variance >= 0 ? 'Under' : 'Over'} Budget
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Project Health</h3>
            {portfolio.projects_on_track > portfolio.projects_at_risk ? (
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            )}
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {portfolio.projects_on_track || 0}/{(portfolio.projects_on_track || 0) + (portfolio.projects_at_risk || 0)}
          </div>
          <p className="text-sm text-gray-600 mt-1">Projects On Track</p>
        </div>
      </div>

      {/* Portfolio Budget Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Budget Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Total Budget (BAC)</p>
            <p className="text-2xl font-bold text-gray-900">
              ${(portfolio.total_budget || 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Forecast at Completion (EAC)</p>
            <p className="text-2xl font-bold text-gray-900">
              ${(portfolio.total_forecast || 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Variance at Completion (VAC)</p>
            <p className={`text-2xl font-bold ${portfolio.total_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {portfolio.total_variance >= 0 ? '+' : ''}${(portfolio.total_variance || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Project Performance</h3>
        </div>
        
        {projects.length === 0 ? (
          <div className="p-12 text-center">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-600">No active projects with EVM data</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SPI
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CPI
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % Complete
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget (BAC)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Forecast (EAC)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variance
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map((project) => (
                  <tr key={project.project_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {project.project_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded ${getHealthColor(project.spi)}`}>
                        {project.spi.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded ${getHealthColor(project.cpi)}`}>
                        {project.cpi.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${Math.min(project.percent_complete, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-900">
                          {project.percent_complete.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                      ${project.budget_at_completion.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                      ${project.estimate_at_completion.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`text-sm font-semibold ${project.variance_at_completion >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {project.variance_at_completion >= 0 ? '+' : ''}${project.variance_at_completion.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getStatusColor(project.status)}`}>
                        {project.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EVM Legend */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-3">EVM Metrics Explained</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <strong className="text-gray-900">SPI (Schedule Performance Index):</strong>
            <p className="text-gray-700 mt-1">
              SPI = EV / PV. Values &gt; 1.0 indicate ahead of schedule.
            </p>
          </div>
          <div>
            <strong className="text-gray-900">CPI (Cost Performance Index):</strong>
            <p className="text-gray-700 mt-1">
              CPI = EV / AC. Values &gt; 1.0 indicate under budget.
            </p>
          </div>
          <div>
            <strong className="text-gray-900">BAC (Budget at Completion):</strong>
            <p className="text-gray-700 mt-1">
              Total planned budget for the project.
            </p>
          </div>
          <div>
            <strong className="text-gray-900">EAC (Estimate at Completion):</strong>
            <p className="text-gray-700 mt-1">
              Forecasted total cost based on current performance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
